import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Paper, Typography } from '@mui/material';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { buildUnauthorizedUrl, isDevLoginSessionLossCode, signedFetch } from '../super-picker/services/api';
import {
  fetchSetupStatus,
  redeemAccessCode,
  fetchMyPaymentRequest,
  fetchPaymentStatus,
  fetchWhatsAppCloudStatus,
  fetchTermsStatus,
  setDemoMode,
  setDemoInterest,
  SetupCheck,
  MyPaymentRequest,
  WhatsAppCloudDailyBudget,
  ConnectorTermsStatus,
} from './services/api';
import SuperConnectorLayout, { ActiveKey, ViewMode } from './components/SuperConnectorLayout';
import SetupChecklistDialog from './components/SetupChecklistDialog';
import TermsAgreementDialog from './components/TermsAgreementDialog';
import PaymentDialog from './components/PaymentDialog';
import PaymentFailureDialog from './components/PaymentFailureDialog';
import PaymentProcessingDialog from './components/PaymentProcessingDialog';
import PaymentSuccessDialog from './components/PaymentSuccessDialog';
import DemoBanner from './components/DemoBanner';
import PremiumConfigGate from './components/PremiumConfigGate';
import PaymentBanner from './components/PaymentBanner';
import BudgetPauseBanner from './components/BudgetPauseBanner';
import ImpersonationBanner from './components/ImpersonationBanner';
import { ConnectorInterestUser } from '../super-picker/dialogs/others/SuperConnectorInterestDialog';
import { MobileMaskProvider } from './context/MobileMaskContext';
import Dashboard from './pages/Dashboard';
import ClientList from './pages/ClientList';
import ClientDetail from './pages/ClientDetail';
import Pipeline from './pages/Pipeline';
import WhySuperConnector from './pages/WhySuperConnector';
import Settings from './pages/Settings';
import MediaLibrary from './pages/MediaLibrary';
import Admin from './pages/Admin';

type LoginUser = { Name?: string; Photo?: string };

function getRouteContext(pathname: string): { activeKey: ActiveKey | null; isClientDetail: boolean } {
  const normalized = pathname.replace(/\/+$/, '') || '/';
  const local = normalized.replace('/super-connector', '') || '/';

  if (local === '/' || local === '/dashboard') return { activeKey: 'dashboard', isClientDetail: false };
  if (local === '/clients') return { activeKey: 'clients', isClientDetail: false };
  if (local.startsWith('/clients/')) return { activeKey: 'clients', isClientDetail: true };
  if (local === '/pipeline') return { activeKey: 'pipeline', isClientDetail: false };
  if (local === '/why') return { activeKey: 'why', isClientDetail: false };
  if (local === '/campaigns') return { activeKey: 'campaigns', isClientDetail: false };
  if (local === '/media') return { activeKey: 'media', isClientDetail: false };
  if (local === '/settings') return { activeKey: 'settings', isClientDetail: false };
  if (local === '/admin') return { activeKey: 'admin', isClientDetail: false };
  return { activeKey: null, isClientDetail: false };
}

// ── ?redirect=<key> deep link ────────────────────────────────────────────────
// External entry points (EDMs, htns.sg links) point at the stable /super-connector
// URL and choose the destination with ?redirect=, e.g. ?redirect=client&id=26 →
// /super-connector/clients/26. Unknown keys are left on the URL untouched.
function resolveRedirectParam(search: string, hash: string): string | null {
  const params = new URLSearchParams(search);
  const key = (params.get('redirect') || '').trim().toLowerCase();
  let target: string | null = null;
  if (key === 'client') {
    const id = Number(params.get('id'));
    if (!Number.isInteger(id) || id <= 0) return null;
    target = `/super-connector/clients/${id}`;
    params.delete('id');
  }
  if (!target) return null;
  // Consume the params so a later back/forward or reload doesn't bounce the user.
  params.delete('redirect');
  const query = params.toString();
  return `${target}${query ? `?${query}` : ''}${hash}`;
}

function JoinPage({ code }: { code: string }) {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleRedeem = async () => {
    setStatus('loading');
    setErrorMsg('');
    try {
      await redeemAccessCode(code);
      setStatus('success');
      setTimeout(() => {
        window.location.replace('/super-connector/dashboard');
      }, 1500);
    } catch (e) {
      setStatus('error');
      setErrorMsg(e instanceof Error ? e.message : 'Failed to redeem code');
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', bgcolor: 'var(--color-bg, #0f1117)' }}>
      <Paper elevation={4} sx={{ p: 4, maxWidth: 420, width: '100%', bgcolor: 'var(--color-surface, #1a1d27)', borderRadius: 3, textAlign: 'center' }}>
        <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>Join Super Connector</Typography>
        <Typography variant="body2" sx={{ color: 'var(--color-text-subtle, #9ca3af)', mb: 3 }}>
          You've been invited to join Super Connector. Click below to activate your access.
        </Typography>
        <Box sx={{ bgcolor: 'var(--color-bg, #0f1117)', borderRadius: 2, px: 3, py: 1.5, mb: 3, display: 'inline-block' }}>
          <Typography variant="h6" fontWeight={700} sx={{ letterSpacing: 2, fontFamily: 'monospace', color: '#ed8936' }}>
            {code}
          </Typography>
        </Box>
        {status === 'error' && <Alert severity="error" sx={{ mb: 2, textAlign: 'left' }}>{errorMsg}</Alert>}
        {status === 'success' && <Alert severity="success" sx={{ mb: 2, textAlign: 'left' }}>Access granted! Redirecting…</Alert>}
        <Button
          variant="contained"
          fullWidth
          size="large"
          onClick={handleRedeem}
          disabled={status === 'loading' || status === 'success'}
          sx={{ bgcolor: '#ed8936', '&:hover': { bgcolor: '#dd7826' }, textTransform: 'none', fontWeight: 700 }}
        >
          {status === 'loading' ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Activate Access Code'}
        </Button>
      </Paper>
    </Box>
  );
}

export function SuperConnectorApp() {
  const location = useLocation();
  const navigate = useNavigate();
  const { activeKey, isClientDetail } = getRouteContext(location.pathname);
  const redirectTo = useMemo(
    () => resolveRedirectParam(location.search, location.hash),
    [location.search, location.hash],
  );
  // 'demo' = logged in but no Agent access → explore sample data instead of a paywall.
  const [accessState, setAccessState] = useState<'checking' | 'granted' | 'demo' | 'denied'>('checking');
  const [loginUser, setLoginUser] = useState<LoginUser | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [setupChecks, setSetupChecks] = useState<SetupCheck[] | null>(null);
  const [setupDialogOpen, setSetupDialogOpen] = useState(false);
  /** Auto-open the checklist at most once per Super Connector session. */
  const hasAutoOpenedSetupChecklist = useRef(false);
  const [currentUser, setCurrentUser] = useState<{ Name?: string; Mobile?: string; Email?: string; CEANumber?: string } | null>(null);
  // Set when this session is an admin logged in as another user (impersonation).
  const [impersonatedBy, setImpersonatedBy] = useState<{ cea: string; name: string } | null>(null);
  const [pendingPayment, setPendingPayment] = useState<MyPaymentRequest | null>(null);
  const [payOpen, setPayOpen] = useState(false);
  const [paymentProcessingOpen, setPaymentProcessingOpen] = useState(false);
  const [paymentSuccessOpen, setPaymentSuccessOpen] = useState(false);
  const [paymentFailureOpen, setPaymentFailureOpen] = useState(false);
  const [dailyBudget, setDailyBudget] = useState<WhatsAppCloudDailyBudget | null>(null);
  const [cloudProviderActive, setCloudProviderActive] = useState(false);
  const [termsStatus, setTermsStatus] = useState<ConnectorTermsStatus | null>(null);
  /** False until the terms check has answered (or failed); setup checklist waits on it. */
  const [termsResolved, setTermsResolved] = useState(false);

  // Admin-only client view mode: 'admin' loads company-name clients, 'non-admin'
  // loads the rest, 'demo' previews sample data. Persisted across reloads.
  const [viewMode, setViewModeState] = useState<ViewMode>(() => {
    try { return (localStorage.getItem('sc-view-mode') as ViewMode) || 'admin'; } catch { return 'admin'; }
  });
  const setViewMode = useCallback((next: ViewMode) => {
    setViewModeState(next);
    try { localStorage.setItem('sc-view-mode', next); } catch { /* ignore */ }
  }, []);

  // No-access users land in Demo (sample data). Live Mode is their own data.
  // Granted users opt into sample data only while the demo preview is selected.
  const prospect = accessState === 'demo';
  // A stored Admin mode from another session must not open Admin for a prospect.
  // They land in Demo until they explicitly choose Live Mode.
  const prospectView: ViewMode = viewMode === 'non-admin' ? 'non-admin' : 'demo';
  const sampleData = prospect
    ? prospectView === 'demo'
    : accessState === 'granted' && viewMode === 'demo';
  const displayViewMode: ViewMode = prospect
    ? prospectView
    : sampleData
      ? 'demo'
      : isAdmin
        ? viewMode
        : 'non-admin';
  const demoView = displayViewMode === 'demo';
  // Granted users — admins and agents alike — can toggle the demo/sample preview.
  const canPreviewDemo = accessState === 'granted';
  // Only an admin viewing "Admin Mode" sees the company-name client set.
  const effectiveAdminMode = isAdmin && displayViewMode === 'admin';
  // Keep the api layer's demo switch in sync BEFORE child pages fetch.
  // Register-interest copy is only for users who do not already have access.
  setDemoMode(sampleData);
  setDemoInterest(prospect);

  const search = new URLSearchParams(location.search);
  const joinCode = search.get('join-code');
  const joinCodeRef = useRef(joinCode);
  const wantsPaymentDialog = search.get('action') === 'paymentrequest';
  const wantsPaymentProcessing = search.get('action') === 'paymentprocessing';
  const wantsPaymentFailure = search.get('payment_failed') === '1';
  const paymentFailureReason = search.get('reason');
  const paymentTxnRef = search.get('ref');
  const paymentReqToken = search.get('req');

  useEffect(() => {
    if (joinCodeRef.current) return;

    let mounted = true;

    const verifyAccess = async () => {
      try {
        const response = await signedFetch('/api/super-connector/user/me');
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          const errorCode = typeof data?.code === 'string' ? data.code : undefined;
          const devLoginRedirectHandled = response.status === 401 && isDevLoginSessionLossCode(errorCode);
          if (mounted) {
            if (errorCode === 'NO_SUPER_CONNECTOR_ACCESS') {
              // No Agent access → show the demo experience instead of a paywall.
              // The 403 still carries the profile so the appointment form can prefill.
              setAccessState('demo');
              setIsAdmin(false);
              if (typeof data?.name === 'string' || typeof data?.identifier === 'string') {
                setCurrentUser({
                  Name: typeof data.name === 'string' ? data.name : undefined,
                  Mobile: typeof data.mobile === 'string' ? data.mobile : undefined,
                  Email: typeof data.email === 'string' ? data.email : undefined,
                  CEANumber: typeof data.identifier === 'string' ? data.identifier : undefined,
                });
                setLoginUser({
                  Name: typeof data.name === 'string' ? data.name : undefined,
                  Photo: typeof data.photo === 'string' ? data.photo : undefined,
                });
              }
            } else {
              setAccessState('denied');
              if (!devLoginRedirectHandled) {
                window.location.replace(
                  buildUnauthorizedUrl(errorCode || 'NO_SUPER_CONNECTOR_ACCESS', { app: 'super-connector' })
                );
              }
            }
          }
          return;
        }
        if (mounted) {
          setAccessState('granted');
          // Keep identity loading app-scoped: /api/users/me requires a
          // Super Picker session and would log out a Super Connector session.
          setCurrentUser({
            Name: data.name ?? data.Name,
            Mobile: data.mobile ?? data.Mobile,
            Email: data.email ?? data.Email,
            CEANumber: data.identifier ?? data.CEANumber,
          });
          setLoginUser({
            Name: data.name ?? data.Name,
            Photo: data.photo ?? data.Photo,
          });
          const role = typeof data.role === 'string' ? data.role : '';
          setIsAdmin(role.trim().toUpperCase() === 'ADMIN');
          setImpersonatedBy(
            data.impersonatedBy && typeof data.impersonatedBy.cea === 'string'
              ? { cea: data.impersonatedBy.cea, name: data.impersonatedBy.name || data.impersonatedBy.cea }
              : null,
          );
        }
      } catch {
        if (mounted) {
          setAccessState('denied');
          window.location.replace(
            buildUnauthorizedUrl('NO_SUPER_CONNECTOR_ACCESS', { app: 'super-connector' })
          );
        }
      }
    };

    verifyAccess();
    return () => { mounted = false; };
  }, []);

  // Outstanding activation payment → drives the payment banner. JWT-only endpoint,
  // reachable in demo too (paying is what grants access).
  useEffect(() => {
    if (joinCodeRef.current) return;
    if (accessState !== 'granted' && accessState !== 'demo') return;
    let active = true;
    fetchMyPaymentRequest()
      .then((r) => { if (active) setPendingPayment(r); })
      .catch(() => { /* non-blocking */ });
    return () => { active = false; };
  }, [accessState]);

  // Terms and conditions: checked on the first live agent page — not in Demo or
  // Admin Mode. An admin logged in as someone else never sees it, so consent is
  // only ever the agent's.
  const termsApplies = accessState === 'granted' && !sampleData && !effectiveAdminMode && !impersonatedBy;
  const loadTermsStatus = useCallback(() => {
    fetchTermsStatus()
      .then((s) => setTermsStatus(s))
      .catch(() => setTermsStatus(null))
      .finally(() => setTermsResolved(true));
  }, []);
  // Re-check every time the live agent view is entered (e.g. Admin Mode →
  // Non-Admin) and whenever an admin saves new terms, so a fresh version that
  // requires re-acknowledgement is picked up without a reload.
  useEffect(() => {
    if (accessState === 'checking') return;
    if (!termsApplies) {
      setTermsResolved(true);
      return;
    }
    setTermsResolved(false);
    loadTermsStatus();
    const onTermsChanged = () => loadTermsStatus();
    window.addEventListener('sc-terms-changed', onTermsChanged);
    return () => window.removeEventListener('sc-terms-changed', onTermsChanged);
  }, [accessState, termsApplies, loadTermsStatus]);
  const termsBlocking = termsApplies && !!termsStatus?.mustAgree;

  // Official API daily budget pause → app-wide banner when cloud sends are halted.
  useEffect(() => {
    if ((accessState !== 'granted' && accessState !== 'demo') || sampleData) {
      setDailyBudget(null);
      setCloudProviderActive(false);
      return;
    }
    let active = true;
    const refresh = () => {
      fetchWhatsAppCloudStatus()
        .then((s) => {
          if (!active) return;
          setCloudProviderActive(s.provider === 'cloud');
          setDailyBudget(s.dailyBudget ?? null);
        })
        .catch(() => { /* non-blocking */ });
    };
    refresh();
    const t = setInterval(refresh, 60_000);
    return () => { active = false; clearInterval(t); };
  }, [accessState, sampleData, location.pathname]);

  // Open the payment dialog when arriving via the emailed CTA (?action=paymentrequest).
  useEffect(() => {
    if (wantsPaymentDialog) setPayOpen(true);
  }, [wantsPaymentDialog]);

  // eNETS sends cancelled, declined, invalid, and otherwise unsuccessful
  // browser returns here. Keep the outstanding payment request available so
  // the user can retry after dismissing the message.
  useEffect(() => {
    if (!wantsPaymentFailure) return;
    setPayOpen(false);
    setPaymentProcessingOpen(false);
    setPaymentSuccessOpen(false);
    setSetupDialogOpen(false);
    setPaymentFailureOpen(true);
  }, [wantsPaymentFailure]);

  // The verified eNETS B2S callback redirects here after the gateway approves
  // the payment. Keep showing "processing" until the linked payment request is
  // confirmed PAID by the backend; the return URL alone is not proof of payment.
  useEffect(() => {
    if (!wantsPaymentProcessing || !paymentTxnRef) return;
    let active = true;
    let pollTimer: ReturnType<typeof setTimeout> | undefined;

    setPayOpen(false);
    setSetupDialogOpen(false);
    setPaymentSuccessOpen(false);
    setPaymentProcessingOpen(true);

    const pollPaymentStatus = async () => {
      try {
        const status = await fetchPaymentStatus(paymentTxnRef);
        if (!active) return;

        if (status.paymentRequestStatus === 'PAID') {
          setPendingPayment(null);
          setPaymentProcessingOpen(false);
          setPaymentSuccessOpen(true);
          navigate(location.pathname, { replace: true });
          return;
        }
      } catch {
        // The callback and database update may still be settling. Keep polling.
      }

      if (active) pollTimer = setTimeout(pollPaymentStatus, 2000);
    };

    pollPaymentStatus();
    return () => {
      active = false;
      if (pollTimer) clearTimeout(pollTimer);
    };
  }, [location.pathname, navigate, paymentTxnRef, wantsPaymentProcessing]);

  // Re-check setup readiness (granted users only). Refresh checks on navigation
  // so the navbar button stays accurate, but auto-open the dialog only once
  // per session when setup is incomplete. Also re-fetch when WhatsApp provider
  // toggles (bridge ↔ Official API) — day readiness depends on that.
  useEffect(() => {
    if (accessState !== 'granted') return;
    let active = true;
    const refresh = (opts?: { allowAutoOpen?: boolean }) => {
      fetchSetupStatus()
        .then((status) => {
          if (!active) return;
          setSetupChecks(status.checks);
          if (!opts?.allowAutoOpen) return;
          const suppressAutoOpen =
            wantsPaymentProcessing
            || wantsPaymentFailure
            || paymentProcessingOpen
            || paymentSuccessOpen
            || paymentFailureOpen
            // The checklist waits until the terms are agreed.
            || !termsResolved
            || termsBlocking;
          if (
            !status.ready
            && !suppressAutoOpen
            && !hasAutoOpenedSetupChecklist.current
          ) {
            hasAutoOpenedSetupChecklist.current = true;
            setSetupDialogOpen(true);
          }
        })
        .catch(() => { /* non-blocking: never gate access on this */ });
    };
    refresh({ allowAutoOpen: true });
    const onProviderChanged = () => refresh();
    window.addEventListener('sc-setup-status-changed', onProviderChanged);
    return () => {
      active = false;
      window.removeEventListener('sc-setup-status-changed', onProviderChanged);
    };
  }, [
    accessState,
    activeKey,
    location.pathname,
    paymentProcessingOpen,
    paymentSuccessOpen,
    paymentFailureOpen,
    wantsPaymentFailure,
    wantsPaymentProcessing,
    termsResolved,
    termsBlocking,
  ]);

  if (joinCode) {
    return <JoinPage code={joinCode} />;
  }

  if (redirectTo) {
    return <Navigate to={redirectTo} replace />;
  }

  if (!activeKey) {
    return <Navigate to="/super-connector/dashboard" replace />;
  }

  // Settings is browsable in demo mode (read-only sample config); Admin is not.
  if ((sampleData || prospect) && activeKey === 'admin') {
    return <Navigate to="/super-connector/dashboard" replace />;
  }

  // Non-admins never reach the admin page even via a direct URL.
  if (activeKey === 'admin' && accessState === 'granted' && !isAdmin) {
    return <Navigate to="/super-connector/dashboard" replace />;
  }

  // "Why Super Connector" is the demo-view pitch. Non-Admin (including a
  // no-access user who switched out of Demo) does not show it.
  if (activeKey === 'why' && (accessState === 'granted' || accessState === 'demo') && !demoView) {
    return <Navigate to="/super-connector/dashboard" replace />;
  }

  if (accessState === 'checking') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', gap: 2 }}>
        <CircularProgress />
        <Typography color="text.secondary">Checking Super Connector access...</Typography>
      </Box>
    );
  }

  if (accessState === 'denied') {
    return null;
  }

  const interestUser: ConnectorInterestUser = {
    name: currentUser?.Name || '',
    mobile: currentUser?.Mobile || '',
    email: currentUser?.Email || '',
    cea: currentUser?.CEANumber || '',
  };
  const showPaymentBanner = !!pendingPayment && !payOpen;
  const showBudgetBanner = !!(
    cloudProviderActive
    && dailyBudget?.paused
    && dailyBudget.limitSgd != null
    && !impersonatedBy
  );
  const showBottomBanner = !!(impersonatedBy || showPaymentBanner || showBudgetBanner || sampleData);

  return (
    // Wraps the layout too — the mobile-mask toggle lives in its avatar menu.
    <MobileMaskProvider>
      <SuperConnectorLayout
        activeKey={activeKey}
        loginUser={loginUser}
        isAdmin={isAdmin}
        canPreviewDemo={canPreviewDemo}
        premiumPreview={prospect}
        viewMode={displayViewMode}
        onViewModeChange={setViewMode}
        demo={demoView}
        bottomBannerOffset={showBottomBanner}
        setupChecks={accessState === 'granted' ? setupChecks : null}
        onOpenSetupChecklist={() => setSetupDialogOpen(true)}
      >
        {/* Keyed on the view mode: switching Admin / Non-Admin / Demo remounts
            the active page so every data fetch re-runs against the new mode
            (a prop change alone doesn't reach pages that fetch only on mount,
            e.g. Dashboard, or the Non-Admin ↔ Demo switch where adminMode
            stays false). */}
        <React.Fragment key={displayViewMode}>
          {activeKey === 'dashboard' && <Dashboard adminMode={effectiveAdminMode} />}
          {activeKey === 'clients' && !isClientDetail && (
            <ClientList isAdmin={isAdmin} adminMode={effectiveAdminMode} />
          )}
          {activeKey === 'clients' && isClientDetail && <ClientDetail />}
          {activeKey === 'pipeline' && <Pipeline adminMode={effectiveAdminMode} />}
          {activeKey === 'why' && demoView && <WhySuperConnector />}
          {activeKey === 'campaigns' && (
            <PremiumConfigGate locked={prospect && !demoView} user={interestUser}>
              <Settings
                key={`campaigns-tab-${typeof (location.state as { tab?: number } | null)?.tab === 'number'
                  ? (location.state as { tab: number }).tab
                  : 0}`}
                mode="campaigns"
                isAdmin={isAdmin && !sampleData}
                initialTab={typeof (location.state as { tab?: number } | null)?.tab === 'number'
                  ? (location.state as { tab: number }).tab
                  : 0}
              />
            </PremiumConfigGate>
          )}
          {activeKey === 'settings' && (
            <PremiumConfigGate locked={prospect && !demoView} user={interestUser}>
              <Settings mode="settings" isAdmin={isAdmin && !sampleData} />
            </PremiumConfigGate>
          )}
          {activeKey === 'media' && (
            <PremiumConfigGate locked={prospect && !demoView} user={interestUser}>
              <MediaLibrary />
            </PremiumConfigGate>
          )}
          {activeKey === 'admin' && isAdmin && !sampleData && <Admin />}
        </React.Fragment>
        {setupChecks && (
          <SetupChecklistDialog
            open={setupDialogOpen}
            checks={setupChecks}
            onClose={() => setSetupDialogOpen(false)}
            onGoToSettings={() => {
              setSetupDialogOpen(false);
              const firstOutstanding = setupChecks.find((c) => !c.ok);
              if (firstOutstanding?.group === 'engagement') {
                navigate('/super-connector/campaigns');
              } else {
                navigate('/super-connector/settings');
              }
            }}
          />
        )}
        {termsBlocking && termsStatus && (
          <TermsAgreementDialog
            status={termsStatus}
            onAgreed={() => setTermsStatus({ ...termsStatus, mustAgree: false, previouslyAgreed: false })}
            onStale={loadTermsStatus}
            onSwitchToDemo={canPreviewDemo ? () => setViewMode('demo') : undefined}
          />
        )}
      </SuperConnectorLayout>

      {/* Bottom banners: impersonation first, then the sample-data bar while
          Demo is on. Payment and budget pause use the slot only outside Demo. */}
      {impersonatedBy ? (
        <ImpersonationBanner
          userName={currentUser?.Name || ''}
          userCea={currentUser?.CEANumber || ''}
          adminName={impersonatedBy.name}
        />
      ) : sampleData ? (
        <DemoBanner user={interestUser} showInterest={prospect} />
      ) : showPaymentBanner ? (
        <PaymentBanner amountSgd={(pendingPayment!.amountCents) / 100} onPay={() => setPayOpen(true)} />
      ) : showBudgetBanner ? (
        <BudgetPauseBanner
          estimatedSpendSgd={dailyBudget!.estimatedSpendSgd}
          limitSgd={dailyBudget!.limitSgd!}
          onOpenSettings={() => navigate('/super-connector/settings')}
        />
      ) : null}

      <PaymentDialog
        open={payOpen}
        onClose={() => {
          setPayOpen(false);
          if (wantsPaymentDialog) navigate(location.pathname, { replace: true });
        }}
        amountSgd={pendingPayment ? pendingPayment.amountCents / 100 : null}
        requestToken={paymentReqToken || pendingPayment?.token || null}
      />

      <PaymentSuccessDialog
        open={paymentSuccessOpen}
        onClose={() => {
          setPaymentSuccessOpen(false);
          navigate(location.pathname, { replace: true });
        }}
      />

      <PaymentProcessingDialog open={paymentProcessingOpen} />

      <PaymentFailureDialog
        open={paymentFailureOpen}
        reason={paymentFailureReason}
        onClose={() => {
          setPaymentFailureOpen(false);
          navigate(location.pathname, { replace: true });
        }}
      />
    </MobileMaskProvider>
  );
}
